import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { Search, Loader2, Download, PackageOpen, TrendingUp, TrendingDown, Layers } from 'lucide-react';
import ReportDateRangeFilter from '../reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';

export default function StockReportTab() {
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [byBatch, setByBatch] = useState(false);
  const [expiringDays, setExpiringDays] = useState('');

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => { const res = await posApi.getCategories(); return res.data.message; },
  });

  const { dateRange, setDateRange, customFrom, setCustomFrom, customTo, setCustomTo, from_date, to_date } =
    useReportDateRange('Today');

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['stockReport', search, filterCategory, dateRange, customFrom, customTo, byBatch, expiringDays],
    queryFn: async () => {
      const res = await posApi.getStockReport({
        item: search || undefined,
        category: filterCategory || undefined,
        from_date: from_date,
        to_date: to_date,
        by_batch: byBatch || expiringDays ? 1 : 0,
        expiring_within_days: expiringDays ? Number(expiringDays) : undefined,
      });
      return res.data.message || [];
    },
  });

  const totals = useMemo(() => {
    if (!reportData?.length) return null;
    return reportData.reduce(
      (acc: { opening: number; stockIn: number; stockOut: number; closing: number }, row: any) => ({
        opening: acc.opening + (row.opening_balance ?? 0),
        stockIn: acc.stockIn + (row.total_stock_in ?? 0),
        stockOut: acc.stockOut + (row.total_stock_out ?? 0),
        closing: acc.closing + (row.available_stock ?? 0),
      }),
      { opening: 0, stockIn: 0, stockOut: 0, closing: 0 },
    );
  }, [reportData]);

  const handleDownloadCSV = () => {
    if (!reportData || reportData.length === 0) return;

    const headers = [
      'Item Code',
      'Item Name',
      'Variant',
      'Batch',
      'Expiry',
      'Category',
      'UOM',
      'Opening Balance',
      'Stock In',
      'Stock Out',
      'Closing Balance',
    ];
    const rows = reportData.map((row: any) => [
      row.item_code,
      `"${row.item_name}"`,
      `"${row.variant_name || ''}"`,
      `"${row.batch_no || ''}"`,
      `"${row.expiry_date || ''}"`,
      `"${row.category_name || row.category || ''}"`,
      `"${row.unit_of_measurement || ''}"`,
      row.opening_balance,
      row.total_stock_in,
      row.total_stock_out,
      row.available_stock,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any[]) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stock_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="card p-5 bg-white shadow-sm border border-slate-200/60 rounded-2xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4 flex-1">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
              className="flex-[2] min-w-[250px]"
            />

            <div className="flex-1 min-w-[200px] space-y-1.5">
              <label className="text-sm font-medium text-gray-700 block">Category</label>
              <select
                className="input-field"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categoriesData?.filter((c: any) => c.category_type === 'Product').map((c: any) => (
                  <option key={c.name} value={c.name}>{c.category_name}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[240px] max-w-md space-y-1.5">
              <label className="text-sm font-medium text-gray-700 block">Search Item</label>
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by item name or code..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={byBatch}
                onChange={(e) => setByBatch(e.target.checked)}
                className="rounded border-gray-300"
              />
              By batch
            </label>
            <div className="min-w-[140px] space-y-1.5">
              <label className="text-sm font-medium text-gray-700 block">Expiring in (days)</label>
              <input
                type="number"
                min="1"
                className="input-field"
                placeholder="e.g. 7"
                value={expiringDays}
                onChange={(e) => {
                  setExpiringDays(e.target.value);
                  if (e.target.value) setByBatch(true);
                }}
              />
            </div>
          </div>

          <button
            onClick={handleDownloadCSV}
            disabled={!reportData || reportData.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm mb-0.5"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="card overflow-hidden border border-slate-200/60 shadow-sm rounded-2xl bg-white">
        <div className="overflow-x-auto hide-scrollbar">
          <table className="w-full text-left border-collapse min-w-[960px]">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="p-4 pl-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Item Details</th>
                <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Variant</th>
                <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Opening</th>
                <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Stock In</th>
                <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Stock Out</th>
                <th className="p-4 pr-6 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-slate-500">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-primary-500" />
                    <p className="text-sm">Compiling stock report...</p>
                  </td>
                </tr>
              ) : reportData?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-slate-500">
                    <PackageOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm font-medium text-slate-600">No stock records found</p>
                    <p className="text-xs text-slate-400 mt-1">Try adjusting your search or category filter.</p>
                  </td>
                </tr>
              ) : (
                reportData?.map((row: any, i: number) => (
                  <tr key={i} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="p-4 pl-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center border border-primary-100/50 shrink-0 group-hover:scale-105 transition-transform">
                          <Layers className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{row.item_name}</div>
                          <div className="text-xs text-slate-500 font-mono mt-0.5">{row.item_code}</div>
                          {(row.batch_no || row.expiry_date) && (
                            <div className="text-[11px] text-amber-700 mt-0.5">
                              {row.batch_no && `Batch ${row.batch_no}`}
                              {row.expiry_date && ` · Exp ${row.expiry_date}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-700">
                      {row.variant_name ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          {row.variant_name}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic text-xs">N/A</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100/50">
                        {row.category_name
                          || categoriesData?.find((c: any) => c.name === row.category)?.category_name
                          || row.category
                          || '—'}
                      </span>
                    </td>
                    <td className="p-4 text-right text-sm font-medium text-slate-700">
                      {row.opening_balance}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1.5 text-emerald-600 font-medium text-sm">
                        <TrendingUp className="w-3.5 h-3.5" />
                        {row.total_stock_in}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1.5 text-rose-600 font-medium text-sm">
                        <TrendingDown className="w-3.5 h-3.5" />
                        {row.total_stock_out}
                      </div>
                    </td>
                    <td className="p-4 pr-6 text-right">
                      <div className={`inline-flex items-center justify-center min-w-[3rem] px-2.5 py-1 rounded-lg text-sm font-bold ${
                        row.available_stock > 10
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
                          : row.available_stock > 0
                          ? 'bg-amber-50 text-amber-700 border border-amber-200/60'
                          : 'bg-rose-50 text-rose-700 border border-rose-200/60'
                      }`}>
                        {row.available_stock}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200 font-semibold text-sm text-slate-800">
                  <td className="p-4 pl-6" colSpan={3}>Totals</td>
                  <td className="p-4 text-right">{totals.opening}</td>
                  <td className="p-4 text-right text-emerald-700">{totals.stockIn}</td>
                  <td className="p-4 text-right text-rose-700">{totals.stockOut}</td>
                  <td className="p-4 pr-6 text-right">{totals.closing}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
